import React, { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import Swal from "sweetalert2";
import toast from "react-hot-toast";
import HttpClientXml from "../utils/HttpClientXml";
import HomeService from "../Services/HomeService";

const headLineStyle = {
  textAlign: "center",
  fontSize: "30px",
  color: "#868e96",
  margin: "15px",
  fontWeight: "bold",
};

function ManageAdvertisementNew() {
  const [advertisements, setAdvertisements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adID, setAdID] = useState("");
  const [isEdit, setIsEdit] = useState(false);
  const [editData, setEditData] = useState({
    city: "",
    media: "",
    description: "", // Added description field
  });
  const [cities, setCities] = useState([]);
  const [mediaType, setMediaType] = useState(""); // "image" or "video"
  const [mediaLoading, setMediaLoading] = useState(false);

  // Function to show description in popup
  const showDescriptionPopup = (description, title = "Description") => {
    Swal.fire({
      title: title,
      html: `
        <div style="text-align: left; max-height: 400px; overflow-y: auto; padding: 10px;">
          <p style="white-space: pre-wrap; word-wrap: break-word;">${
            description || "No description available"
          }</p>
        </div>
      `,
      showCloseButton: true,
      showConfirmButton: false,
      width: "600px",
      customClass: {
        popup: "description-popup",
      },
    });
  };

  const columns = [
    {
      name: "S.No.",
      selector: (row, index) => index + 1,
      sortable: true,
      width: "80px",
    },
    {
      name: "City",
      selector: (row) => row.city?.name || "N/A",
      sortable: true,
      center: true,
    },
    {
      name: "Description",
      cell: (row) => {
        const description = row.description || "";
        const shortDescription =
          description.length > 10
            ? `${description.substring(0, 10)}...`
            : description;

        return (
          <div className="d-flex align-items-center">
            <span className="me-2" style={{ minWidth: "50px" }}>
              {shortDescription || (
                <span className="text-muted">No description</span>
              )}
            </span>
            {description.length > 10 && (
              <button
                type="button"
                className="btn btn-sm p-0"
                onClick={() =>
                  showDescriptionPopup(description, "Advertisement Description")
                }
                title="View full description"
                style={{
                  background: "transparent",
                  border: "none",
                  color: "#007bff",
                  fontSize: "16px",
                }}
              >
                <i className="fa-solid fa-eye"></i>
              </button>
            )}
          </div>
        );
      },
      center: true,
      sortable: true,
      sortFunction: (rowA, rowB) => {
        const descA = rowA.description || "";
        const descB = rowB.description || "";
        return descA.localeCompare(descB);
      },
    },
    {
      name: "Media",
      cell: (row) => {
        const mediaUrl = row.media;
        const isImage =
          mediaUrl && /\.(jpg|jpeg|png|gif|webp)$/i.test(mediaUrl);
        const isVideo = mediaUrl && /\.(mp4|avi|mov|webm)$/i.test(mediaUrl);

        return (
          <div style={{ position: "relative" }}>
            {isImage ? (
              <img
                src={mediaUrl || "/placeholder-image.png"}
                alt="Advertisement"
                width="80"
                height="60"
                style={{
                  borderRadius: "8px",
                  objectFit: "cover",
                  border: "1px solid #dee2e6",
                }}
              />
            ) : isVideo ? (
              <>
                <video
                  width="80"
                  height="60"
                  style={{
                    borderRadius: "8px",
                    objectFit: "cover",
                    border: "1px solid #dee2e6",
                  }}
                  preload="metadata"
                >
                  <source src={mediaUrl} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
                <div
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    background: "rgba(0,0,0,0.7)",
                    borderRadius: "50%",
                    width: "30px",
                    height: "30px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                  }}
                  onClick={() => window.open(mediaUrl, "_blank")}
                >
                  <i
                    className="fa-solid fa-play"
                    style={{
                      color: "white",
                      fontSize: "12px",
                      marginLeft: "2px",
                    }}
                  ></i>
                </div>
              </>
            ) : (
              <span className="text-muted">No Media</span>
            )}
          </div>
        );
      },
      center: true,
    },
    {
      name: "Media Type",
      cell: (row) => {
        const mediaUrl = row.media;
        const isImage =
          mediaUrl && /\.(jpg|jpeg|png|gif|webp)$/i.test(mediaUrl);
        const isVideo = mediaUrl && /\.(mp4|avi|mov|webm)$/i.test(mediaUrl);

        return (
          <span
            className={`badge ${
              isImage ? "bg-success" : isVideo ? "bg-primary" : "bg-secondary"
            }`}
          >
            {isImage ? "Image" : isVideo ? "Video" : "Unknown"}
          </span>
        );
      },
      center: true,
    },
    {
      name: "Created Date",
      selector: (row) =>
        new Date(row.createdAt || row.createdOn).toLocaleDateString(),
      sortable: true,
      center: true,
    },
    {
      name: "Action",
      cell: (row) => (
        <div>
          <button
            title="Edit"
            onClick={() => handleEdit(row)}
            className="btn btn-sm me-2"
            style={{
              background: "transparent",
              border: "none",
              color: "rgb(133, 218, 79)",
            }}
          >
            <i className="fa-solid fa-pen-to-square fs-5"></i>
          </button>
          <button
            title="Delete"
            onClick={() => handleDelete(row._id)}
            className="btn btn-sm"
            style={{
              background: "transparent",
              border: "none",
              color: "#dc3545",
            }}
          >
            <i className="fa-solid fa-trash-can fs-5"></i>
          </button>
        </div>
      ),
      center: true,
    },
  ];

  // Fetch cities for dropdown
  const fetchCities = async () => {
    try {
      const res = await HomeService.getCities();
      if (res?.data) {
        setCities(res.data);
      }
    } catch (error) {
      console.error("Error fetching cities:", error);
    }
  };

  const fetchAllAdvertisements = async () => {
    setLoading(true);
    try {
      const res = await HomeService.getAdvertisementNew();
      console.log("Advertisements data:", res);
      if (res?.data) {
        setAdvertisements(res.data);
      } else {
        toast.error("Failed to load advertisements!");
        setAdvertisements([]);
      }
    } catch (error) {
      console.error("Error fetching advertisements:", error);
      toast.error("Failed to load advertisements!");
      setAdvertisements([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllAdvertisements();
    fetchCities();
  }, []);

  const handleEdit = (row) => {
    console.log("Editing advertisement:", row);
    setIsEdit(true);

    // Determine media type based on URL extension
    const mediaUrl = row.media;
    const isImage = mediaUrl && /\.(jpg|jpeg|png|gif|webp)$/i.test(mediaUrl);
    const detectedType = isImage ? "image" : "video";

    setEditData({
      city: row.city?._id || row.city || "",
      media: mediaUrl || "",
      description: row.description || "", // Added description
    });
    setMediaType(detectedType);
    setAdID(row._id);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Delete Advertisement?",
      text: "Are you sure you want to delete this advertisement?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      confirmButtonColor: "#d33",
      cancelButtonText: "Cancel",
      reverseButtons: true,
    });

    if (result.isConfirmed) {
      try {
        const deleteResult = await HomeService.deleteAdvertisementNew(id);
        console.log("Delete result:", deleteResult);

        if (deleteResult?.status) {
          Swal.fire(
            "Deleted!",
            "Advertisement has been deleted successfully.",
            "success"
          );
          fetchAllAdvertisements();
        } else {
          Swal.fire(
            "Error!",
            deleteResult?.message || "Failed to delete advertisement.",
            "error"
          );
        }
      } catch (error) {
        console.error("Delete error:", error);
        Swal.fire(
          "Error!",
          "Failed to delete advertisement. Please try again.",
          "error"
        );
      }
    }
  };

  const handleMediaUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Determine media type
    let type = "";
    if (file.type.startsWith("image/")) {
      type = "image";
    } else if (file.type.startsWith("video/")) {
      type = "video";
    } else {
      toast.error("Please select a valid image or video file");
      return;
    }

    // Validate file size based on type
    // const maxSize = type === "image" ? 5 * 1024 * 1024 : 50 * 1024 * 1024;
    // if (file.size > maxSize) {
    //   toast.error(
    //     `${type === "image" ? "Image" : "Video"} size should be less than ${
    //       maxSize / (1024 * 1024)
    //     }MB`
    //   );
    //   return;
    // }

    setMediaLoading(true);
    setMediaType(type);

    const data = new FormData();
    data.append("image", file);

    try {
      const res = await HttpClientXml.fileUplode("image-upload", "POST", data);
      console.log("Media upload response:", res);

      if (res?.image) {
        setEditData((prev) => ({ ...prev, media: res.image }));
        toast.success(
          `${type === "image" ? "Image" : "Video"} uploaded successfully!`
        );
      } else {
        toast.error(res?.message || `Failed to upload ${type}`);
      }
    } catch (error) {
      console.error("Error uploading media:", error);
      toast.error(`Failed to upload ${type}. Please try again.`);
    } finally {
      setMediaLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!editData.city) {
      toast.error("Please select a city");
      return;
    }
    if (!editData.media) {
      toast.error("Please upload a media file (image or video)");
      return;
    }
    if (!editData.description.trim()) {
      // Added validation for description
      toast.error("Please enter a description");
      return;
    }

    console.log("Updating advertisement with data:", editData);

    try {
      const res = await HomeService.updateAdvertisementNew(adID, editData);
      console.log("Update API response:", res);

      if (res?.status) {
        toast.success("Advertisement updated successfully!");
        setIsEdit(false);
        setEditData({ city: "", media: "", description: "" });
        setMediaType("");
        setAdID("");
        fetchAllAdvertisements();
      } else {
        toast.error(res?.message || "Failed to update advertisement.");
      }
    } catch (error) {
      console.error("Error updating advertisement:", error);
      toast.error("Failed to update advertisement. Please try again.");
    }
  };

  const handleCancel = () => {
    setIsEdit(false);
    setEditData({ city: "", media: "", description: "" });
    setMediaType("");
    setAdID("");
    toast.success("Edit cancelled.");
  };

  const removeMedia = () => {
    setEditData((prev) => ({ ...prev, media: "" }));
    setMediaType("");
    toast.success("Media removed");
  };

  return (
    <>
      <div className="main_wrap" style={{ marginTop: "6rem" }}>
        <div className="container-fluid">
          {isEdit ? (
            <section className="chartBox_area">
              <div className="card">
                <div className="card-header">
                  <h4 className="mb-0">Edit Advertisement</h4>
                </div>
                <div className="card-body">
                  <form onSubmit={handleSubmit}>
                    <div className="row">
                      {/* City Selection */}
                      <div className="col-md-6 mt-3">
                        <label>
                          City<span className="text-danger">*</span>
                        </label>
                        <select
                          className="form-control"
                          name="city"
                          value={editData?.city || ""}
                          onChange={(e) =>
                            setEditData((prev) => ({
                              ...prev,
                              city: e.target.value,
                            }))
                          }
                          required
                        >
                          <option value="" disabled>
                            ---Select City---
                          </option>
                          {cities.map((city) => (
                            <option key={city._id} value={city._id}>
                              {city.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Media Upload Section */}
                      <div className="col-md-6 mt-3">
                        <label>
                          Advertisement Media (Image or Video)
                          <span className="text-danger">*</span>
                        </label>
                        {editData.media && (
                          <div className="mt-2 mb-3 position-relative">
                            {mediaType === "image" ? (
                              <img
                                src={editData.media}
                                alt="Media Preview"
                                style={{
                                  maxHeight: "150px",
                                  maxWidth: "100%",
                                  borderRadius: "8px",
                                  border: "2px solid #dee2e6",
                                }}
                              />
                            ) : (
                              <video
                                controls
                                style={{
                                  maxHeight: "150px",
                                  maxWidth: "100%",
                                  borderRadius: "8px",
                                  border: "2px solid #dee2e6",
                                }}
                              >
                                <source src={editData.media} type="video/mp4" />
                                Your browser does not support the video tag.
                              </video>
                            )}
                            <button
                              type="button"
                              onClick={removeMedia}
                              className="btn btn-danger btn-sm position-absolute"
                              style={{ top: "5px", right: "5px" }}
                            >
                              <i className="fa-solid fa-times"></i>
                            </button>
                          </div>
                        )}
                        <input
                          type="file"
                          className="form-control"
                          onChange={handleMediaUpload}
                          accept="image/*,video/*"
                          disabled={mediaLoading}
                        />
                        <div className="form-text">
                          {mediaLoading ? (
                            <span className="text-primary">
                              <i className="fa-solid fa-spinner fa-spin me-1"></i>
                              Uploading {mediaType}...
                            </span>
                          ) : (
                            ""
                          )}
                        </div>
                      </div>

                      {/* Description Textarea */}
                      <div className="col-12 mt-3">
                        <label htmlFor="description">
                          Description<span className="text-danger">*</span>
                        </label>
                        <textarea
                          id="description"
                          className="form-control"
                          name="description"
                          value={editData?.description || ""}
                          onChange={(e) =>
                            setEditData((prev) => ({
                              ...prev,
                              description: e.target.value,
                            }))
                          }
                          rows="4"
                          placeholder="Enter advertisement description..."
                          required
                        ></textarea>
                        <div className="form-text">
                          Enter a detailed description for this advertisement
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 pt-3 border-top">
                      <button
                        type="submit"
                        className="btn btn-primary me-3"
                        style={{
                          background: "rgb(133, 218, 79)",
                          border: "none",
                          padding: "10px 25px",
                          fontWeight: "500",
                        }}
                        disabled={mediaLoading}
                      >
                        <i className="fa-solid fa-floppy-disk me-2"></i>
                        Save Changes
                      </button>
                      <button
                        type="button"
                        onClick={handleCancel}
                        className="btn btn-secondary"
                        style={{
                          padding: "10px 25px",
                          fontWeight: "500",
                        }}
                      >
                        <i className="fa-solid fa-times me-2"></i>
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </section>
          ) : (
            <>
              <div className="mt-5">
                <div style={headLineStyle} className="page-headline">
                  Manage Advertisements
                </div>
                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2">Loading advertisements...</p>
                  </div>
                ) : advertisements.length > 0 ? (
                  <div className="card">
                    <div className="card-body">
                      <DataTable
                        columns={columns}
                        data={advertisements}
                        pagination
                        responsive
                        highlightOnHover
                        striped
                        paginationPerPage={10}
                        paginationRowsPerPageOptions={[10, 20, 30, 50]}
                        noDataComponent={
                          <div className="text-center py-4">
                            <i className="fa-solid fa-ad fa-2x text-muted mb-3"></i>
                            <p>No advertisements found</p>
                          </div>
                        }
                      />
                    </div>
                  </div>
                ) : (
                  <div className="card">
                    <div className="card-body text-center py-5">
                      <i className="fa-solid fa-ad fa-3x text-muted mb-3"></i>
                      <h5>No Advertisements Available</h5>
                      <p className="text-muted">
                        There are no advertisements to display at the moment.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Add CSS for the popup */}
      <style>
        {`
          .description-popup .swal2-popup {
            max-width: 600px;
          }
          .description-popup .swal2-html-container {
            overflow: hidden;
          }
        `}
      </style>
    </>
  );
}

export default ManageAdvertisementNew;
